import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import Logo from '../Logo';
import { BiTransferAlt } from "react-icons/bi";
import { useTranslation } from 'react-i18next';
import LogoutHelper from '../../../../helpers/LogoutHelper';
import { useDispatch, useSelector } from 'react-redux';
import { clearFilters, playSong } from '../../actions/common.actions';
import { selectLoginData } from "../../../pages/login/login.actions";
import { setSidebarOpen, selectSidebar } from "./sidebar.actions";
import { IoMdHome } from "react-icons/io";
import { GoGear } from "react-icons/go";
import { RiLogoutBoxLine } from "react-icons/ri";
import { CiCreditCard2 } from "react-icons/ci";
import { IoStatsChart, IoFootball, IoTrophy } from "react-icons/io5";
import { MdHistory, MdOutlineRule } from "react-icons/md";


export const SideBarTwo = () => {
    const { t } = useTranslation();
    const { userDetails } = useSelector(selectLoginData);
    const { openSideBar } = useSelector(selectSidebar);
    const sidebarRef = useRef(null);
    const dispatch = useDispatch();
    const location = useLocation();

    const handleSongClick = async () => {
        dispatch(playSong());
    };

    useEffect(() => {
        const handleClick = (e) => {
            if (
                sidebarRef.current &&
                !sidebarRef.current.contains(e.target) &&
                !e.target.classList.contains('toggle') &&
                openSideBar
            ) {
                dispatch(setSidebarOpen(false)); // Close sidebar
            }
        };

        window.addEventListener('click', handleClick);
        return () => {
            window.removeEventListener('click', handleClick);
        };
    }, [dispatch, openSideBar]);

    const handleAccount = (e) => {
        handleSongClick();
        dispatch(setSidebarOpen(false)); // Close sidebar after navigation
    };

    const navigationItems = [
        {
            to: "/dashboard",
            icon: IoMdHome,
            label: "HOME",
            description: "Dashboard overview"
        },
        {
            to: "/maungg",
            icon: IoFootball,
            label: t('maungg'),
            description: "Football betting"
        },
        {
            to: "/body",
            icon: IoStatsChart,
            label: t('bodyGoal'),
            description: "Match analysis"
        },
        {
            to: "/selected-matches",
            icon: IoTrophy,
            label: `${t('selected')} ${t('matches')}`,
            description: "Your picks"
        },
        {
            to: "/old-histories",
            icon: MdHistory,
            label: t('oldHistories'),
            description: "Betting history"
        },
        {
            to: "/account",
            icon: BiTransferAlt,
            label: t('statement'),
            description: "Account details"
        },
        {
            to: "/rules",
            icon: MdOutlineRule,
            label: t('rule'),
            description: "Terms & conditions"
        }
    ];

    if (userDetails?.userId === 2889) {
        navigationItems.push({
            to: "/transactions",
            icon: CiCreditCard2,
            label: t("transaction"),
            description: "Payment history"
        });
    }

    const isActiveRoute = (path) => {
        return location.pathname === path;
    };

    return (
        <div ref={sidebarRef} className="fixed inset-0 z-[1000] lg:relative lg:inset-auto">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black bg-opacity-50 lg:hidden"
                onClick={() => dispatch(setSidebarOpen(false))}
            />

            {/* Sidebar */}
            <div className="relative w-80 max-w-[85vw] h-full bg-white shadow-xl flex flex-col">
                {/* Header */}
                <div className="flex flex-col items-center px-6 py-6 bg-gradient-to-br from-primary to-[#1F5F1F] text-white relative">
                    {/* Close Button for Mobile */}
                    <button
                        onClick={() => dispatch(setSidebarOpen(false))}
                        className="lg:hidden absolute top-4 right-4 p-2 rounded-lg bg-white bg-opacity-20 active:bg-opacity-30"
                        aria-label="Close navigation menu"
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"/>
                        </svg>
                    </button>

                    <Logo isSidebar={true} />

                    {/* User Profile Card */}
                    <div className="mt-4 w-full bg-white bg-opacity-20 rounded-xl p-2">
                        <div className="flex items-center space-x-3">
                            <div className="w-12 h-12 bg-white bg-opacity-30 rounded-full flex items-center justify-center">
                                <span className="text-lg font-bold">
                                    {userDetails?.username?.charAt(0)?.toUpperCase() || 'U'}
                                </span>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-semibold truncate">
                                    {userDetails?.username || 'User'}
                                </p>
                                <p className="text-xs opacity-75">
                                    Balance: {userDetails?.balance ? parseInt(userDetails.balance).toLocaleString() : '0'} Ks
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation */}
                <div className="flex-1 overflow-y-auto py-6">
                    <nav className="px-4 space-y-2">
                        {navigationItems.map((item, index) => {
                            const Icon = item.icon;
                            const isActive = isActiveRoute(item.to);

                            return (
                                <Link
                                    key={index}
                                    to={item.to}
                                    onClick={handleAccount}
                                    className={`group flex items-center p-3 rounded-xl ${
                                        isActive
                                            ? 'bg-primary text-white shadow-sm'
                                            : 'text-gray-700 active:bg-gray-100'
                                    }`}
                                >
                                    <Icon
                                        size={24}
                                        className={`flex-shrink-0 ${
                                            isActive ? 'text-white' : 'text-gray-500 group-hover:text-primary'
                                        }`}
                                    />
                                    <div className="ml-4 flex-1 min-w-0">
                                        <p className={`text-sm font-semibold ${
                                            isActive ? 'text-white' : 'text-gray-900'
                                        }`}>
                                            {item.label}
                                        </p>
                                        <p className={`text-xs truncate ${
                                            isActive ? 'text-gray-200' : 'text-gray-500'
                                        }`}>
                                            {item.description}
                                        </p>
                                    </div>
                                    {isActive && (
                                        <div className="w-2 h-2 bg-accent rounded-full"></div>
                                    )}
                                </Link>
                            );
                        })}
                    </nav>
                </div>

                {/* Footer - Settings and Logout */}
                <div className="px-4 py-4 border-t border-gray-200 space-y-2">
                    <Link
                        to="/setting"
                        onClick={handleAccount}
                        className={`group flex items-center p-3 rounded-xl ${
                            isActiveRoute('/setting')
                                ? 'bg-primary text-white shadow-sm'
                                : 'text-gray-700 active:bg-gray-100'
                        }`}
                    >
                        <GoGear
                            size={24}
                            className={`flex-shrink-0 ${
                                isActiveRoute('/setting') ? 'text-white' : 'text-gray-500 group-hover:text-primary'
                            }`}
                        />
                        <span className="ml-4 text-sm font-semibold">
                            {t("setting")}
                        </span>
                    </Link>

                    <button
                        onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            await handleSongClick();
                            dispatch(clearFilters());
                            dispatch(setSidebarOpen(false)); // Close sidebar
                            LogoutHelper();
                        }}
                        className="w-full group flex items-center p-3 rounded-xl text-gray-700 active:bg-red-50 active:text-red-600"
                    >
                        <RiLogoutBoxLine
                            size={24}
                            className="flex-shrink-0 text-gray-500 group-hover:text-red-600"
                        />
                        <span className="ml-4 text-sm font-semibold">
                            {t("logout")}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    );
};
